/**
 * Layering [New] — desktop scroll reveal
 * Matches Layering.jsx useScroll offset ["start 0.85", "center 0.5"]
 * + spring { stiffness: 90, damping: 24, mass: 0.4 }
 * + transforms leftX/rightX/sideOpacity/sideScale
 */
(function () {
  'use strict';

  var SECTION_TYPE = 'layering-new';
  var MQ = '(min-width: 768px)';
  var instances = new WeakMap();

  function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function mapRange(value, inMin, inMax, outMin, outMax) {
    if (inMax === inMin) return outMin;
    var t = clamp((value - inMin) / (inMax - inMin), 0, 1);
    return lerp(outMin, outMax, t);
  }

  function mapOpacity(p) {
    if (p <= 0.15) return 0;
    if (p >= 0.9) return 1;
    if (p < 0.5) return mapRange(p, 0.15, 0.5, 0, 0.75);
    return mapRange(p, 0.5, 0.9, 0.75, 1);
  }

  function LayeringNew(root) {
    this.root = root;
    this.stage = root.querySelector('[data-ly-stage]');
    this.left = root.querySelector('[data-ly-card="left"]');
    this.right = root.querySelector('[data-ly-card="right"]');
    this.progress = 0;
    this.current = 0;
    this.raf = null;
    this.reducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.desktop =
      typeof window.matchMedia === 'function' && window.matchMedia(MQ).matches;

    if (this.reducedMotion) {
      root.classList.add('is-reduced-motion');
      this.snapToEnd();
      return;
    }

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.tick = this.tick.bind(this);

    this.mql = window.matchMedia(MQ);
    if (this.mql.addEventListener) {
      this.mql.addEventListener('change', this.onResize);
    } else if (this.mql.addListener) {
      this.mql.addListener(this.onResize);
    }

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });

    this.onResize();
  }

  LayeringNew.prototype.snapToEnd = function () {
    this.apply(1);
  };

  LayeringNew.prototype.getProgress = function () {
    if (!this.root) return 0;
    var rect = this.root.getBoundingClientRect();
    var vh = window.innerHeight || 1;
    /* Framer offset: start when section top hits 85% of viewport → end when section center hits 50% */
    var start = vh * 0.85 - rect.top;
    var end = vh * 0.5 - (rect.top + rect.height / 2);
    var range = start - end;
    if (range <= 0) return rect.top < vh * 0.5 ? 1 : 0;
    return clamp((start - 0) / range, 0, 1);
  };

  /* Re-derive progress with explicit start/end Y positions in document space */
  LayeringNew.prototype.getProgressDoc = function () {
    var rect = this.root.getBoundingClientRect();
    var scrollY = window.scrollY || window.pageYOffset || 0;
    var vh = window.innerHeight || 1;
    var sectionTop = rect.top + scrollY;
    var sectionCenter = sectionTop + rect.height / 2;

    var startScroll = sectionTop - vh * 0.85;
    var endScroll = sectionCenter - vh * 0.5;
    if (endScroll <= startScroll) return scrollY >= endScroll ? 1 : 0;
    return clamp((scrollY - startScroll) / (endScroll - startScroll), 0, 1);
  };

  LayeringNew.prototype.apply = function (p) {
    var leftX = mapRange(p, 0.15, 0.9, 0, -108);
    var rightX = mapRange(p, 0.15, 0.9, 0, 108);
    var opacity = mapOpacity(p);
    var scale = mapRange(p, 0.15, 0.9, 0.94, 1);

    if (this.left) {
      this.left.style.opacity = String(opacity);
      this.left.style.transform =
        'translate3d(' + leftX + '%, 0, 0) scale(' + scale + ')';
    }
    if (this.right) {
      this.right.style.opacity = String(opacity);
      this.right.style.transform =
        'translate3d(' + rightX + '%, 0, 0) scale(' + scale + ')';
    }
  };

  LayeringNew.prototype.onScroll = function () {
    if (!this.desktop || this.reducedMotion) return;
    this.progress = this.getProgressDoc();
    if (!this.raf) this.raf = window.requestAnimationFrame(this.tick);
  };

  LayeringNew.prototype.tick = function () {
    this.raf = null;
    /* Approximate Framer spring: stiffness 90, damping 24, mass 0.4 */
    var delta = this.progress - this.current;
    this.current += delta * 0.12;
    if (Math.abs(delta) > 0.0005) {
      this.apply(this.current);
      this.raf = window.requestAnimationFrame(this.tick);
    } else {
      this.current = this.progress;
      this.apply(this.current);
    }
  };

  LayeringNew.prototype.onResize = function () {
    this.desktop =
      typeof window.matchMedia === 'function' && window.matchMedia(MQ).matches;
    if (!this.desktop) {
      if (this.left) {
        this.left.style.opacity = '';
        this.left.style.transform = '';
      }
      if (this.right) {
        this.right.style.opacity = '';
        this.right.style.transform = '';
      }
      return;
    }
    this.progress = this.getProgressDoc();
    this.current = this.progress;
    this.apply(this.current);
  };

  LayeringNew.prototype.destroy = function () {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.mql) {
      if (this.mql.removeEventListener) {
        this.mql.removeEventListener('change', this.onResize);
      } else if (this.mql.removeListener) {
        this.mql.removeListener(this.onResize);
      }
    }
    if (this.raf) {
      window.cancelAnimationFrame(this.raf);
      this.raf = null;
    }
  };

  function initRoot(root) {
    if (!root || instances.has(root)) return;
    instances.set(root, new LayeringNew(root));
  }

  function destroyRoot(root) {
    if (!root || !instances.has(root)) return;
    instances.get(root).destroy();
    instances.delete(root);
  }

  function initAll(scope) {
    (scope || document)
      .querySelectorAll('[data-section-type="' + SECTION_TYPE + '"]')
      .forEach(initRoot);
  }

  function findRoot(sectionEl) {
    if (!sectionEl) return null;
    if (sectionEl.matches && sectionEl.matches('[data-section-type="' + SECTION_TYPE + '"]')) {
      return sectionEl;
    }
    return sectionEl.querySelector
      ? sectionEl.querySelector('[data-section-type="' + SECTION_TYPE + '"]')
      : null;
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  if (document.readyState !== 'loading') {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = findRoot(event.target);
    if (!root) return;
    destroyRoot(root);
    initRoot(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = findRoot(event.target);
    if (root) destroyRoot(root);
  });
})();
